// @ts-check

/**
 * @typedef {{ width: number, height: number }} Size
 * @typedef {{ scale: number, scaledWidth: number, scaledHeight: number, outputWidth: number, outputHeight: number }} WatermarkOutputSize
 * @typedef {{ char: string, x: number, y: number, angle: number }} GlyphLayout
 * @typedef {{ glyphs: GlyphLayout[], rotation: number }} TextLayout
 * @typedef {{ period: number, phase: number, amplitude: number, weight: number }} Wave
 * @typedef {{ x: number, y: number, radius: number, strength: number }} Bulge
 * @typedef {{ canvas: HTMLCanvasElement, alpha: number }} WatermarkLayer
 * @typedef {{ imageCanvas: HTMLCanvasElement, watermarkText: string }} WatermarkInput
 */

const WATERMARK_CONFIG = {
  borderSize: 20,
  bottomBorderSize: 110,
  maxDimension: 1000,
  defaultText: "CONTROLLED COPY",
  macro: {
    fontFamily: "Courier New, ui-monospace, monospace",
    fontSize: 18,
    alpha: 0.65,
    waveCount: 2,
    minPeriod: 1 / 6,
    maxPeriod: 1 / 12,
    minAmplitudeMultiplier: 0.6,
    maxAmplitudeMultiplier: 1.8,
    rowOffsetMultiplier: 0.5,
  },
  micro: {
    fontFamily: "Courier New, ui-monospace, monospace",
    fontSize: 7,
    alpha: 0.22,
    lineSpacing: 11,
    angleOffset: Math.PI / 3,
  },
  bulge: {
    minRadius: 160,
    maxRadius: 420,
    minDistance: 260,
    minStrength: 0.08,
    maxStrength: 0.42,
    targetDensity: 1.35,
    attemptsPerBulge: 60,
  },
  flow: {
    amplitude: 9,
    frequencyX: 1.7,
    frequencyY: 2.3,
  },
  bottomText: {
    fontFamily: "Arial, ui-sans-serif, system-ui, sans-serif",
    maxFontSize: 24,
    minFontSize: 13,
    strokeWidth: 8,
  },
};

const CHANNEL_COUNT = 4;
const RED_CHANNEL = 0;
const GREEN_CHANNEL = 1;
const BLUE_CHANNEL = 2;
const ALPHA_CHANNEL = 3;
const MAX_CHANNEL_VALUE = 255;
const LUMINANCE_RED = 0.299;
const LUMINANCE_GREEN = 0.587;
const LUMINANCE_BLUE = 0.114;
const LUMINANCE_THRESHOLD = 127.5;
const FULL_CIRCLE = Math.PI * 2;
const HALF_TURN_START = -Math.PI / 2;

/**
 * @param {WatermarkInput} input
 * @returns {Promise<HTMLCanvasElement>}
 */
export async function applyWatermarkToCanvas(input) {
  const watermarkText = normalizeWatermarkText(input.watermarkText);
  const outputSize = computeWatermarkOutputSize(
    input.imageCanvas.width,
    input.imageCanvas.height,
  );
  const random = random01;
  const baseCanvas = drawBaseDocument(input.imageCanvas, outputSize);
  const macroLayer = drawWavyTextLayer(
    outputSize.outputWidth,
    outputSize.outputHeight,
    watermarkText,
    random,
  );
  const microLayer = drawMicrotextLayer(
    outputSize.outputWidth,
    outputSize.outputHeight,
    watermarkText,
    random,
  );
  const distortedMacro = distortLayer(macroLayer, random);
  const distortedMicro = distortLayer(microLayer, random);
  const layers = [
    {
      canvas: distortedMacro,
      alpha: WATERMARK_CONFIG.macro.alpha,
    },
    {
      canvas: distortedMicro,
      alpha: WATERMARK_CONFIG.micro.alpha,
    },
  ];

  composeAdaptiveWatermarkLayers(baseCanvas, layers);
  drawBottomWatermarkCode(baseCanvas, input.watermarkText);

  return baseCanvas;
}

/**
 * @param {number} inputWidth
 * @param {number} inputHeight
 * @returns {WatermarkOutputSize}
 */
export function computeWatermarkOutputSize(inputWidth, inputHeight) {
  const largestDimension = Math.max(1, inputWidth, inputHeight);
  const scale = WATERMARK_CONFIG.maxDimension / largestDimension;
  const scaledWidth = Math.max(1, Math.round(inputWidth * scale));
  const scaledHeight = Math.max(1, Math.round(inputHeight * scale));
  const outputWidth = scaledWidth + WATERMARK_CONFIG.borderSize * 2;
  const outputHeight =
    scaledHeight +
    WATERMARK_CONFIG.borderSize +
    WATERMARK_CONFIG.bottomBorderSize;

  return { scale, scaledWidth, scaledHeight, outputWidth, outputHeight };
}

/**
 * @param {string} text
 * @returns {string}
 */
export function normalizeWatermarkText(text) {
  let result = text.trim().replace(/(?:[.,!?;:]|\s+)$/, "").toUpperCase();
  if (result.length === 0) {
    result = WATERMARK_CONFIG.defaultText;
  }

  return result;
}

/**
 * @param {HTMLCanvasElement} imageCanvas
 * @param {WatermarkOutputSize} size
 * @returns {HTMLCanvasElement}
 */
function drawBaseDocument(imageCanvas, size) {
  const canvas = createCanvas(size.outputWidth, size.outputHeight);
  const ctx = get2dContext(canvas);
  ctx.save();
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, size.outputWidth, size.outputHeight);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    imageCanvas,
    WATERMARK_CONFIG.borderSize,
    WATERMARK_CONFIG.borderSize,
    size.scaledWidth,
    size.scaledHeight,
  );
  ctx.restore();
  return canvas;
}

/**
 * @param {number} width
 * @param {number} height
 * @param {string} text
 * @param {() => number} random
 * @returns {HTMLCanvasElement}
 */
function drawWavyTextLayer(width, height, text, random) {
  const canvas = createCanvas(width, height);
  const ctx = get2dContext(canvas);
  const layout = layoutWavyText(ctx, width, height, text, random);

  ctx.save();
  ctx.clearRect(0, 0, width, height);
  ctx.font = `${WATERMARK_CONFIG.macro.fontSize}px ${WATERMARK_CONFIG.macro.fontFamily}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#000000";
  ctx.translate(width / 2, height / 2);
  ctx.rotate(layout.rotation);

  for (let i = 0, total = layout.glyphs.length; i < total; i++) {
    const glyph = layout.glyphs[i];
    ctx.save();
    ctx.translate(glyph.x, glyph.y);
    ctx.rotate(glyph.angle);
    ctx.fillText(glyph.char, 0, 0);
    ctx.restore();
  }

  ctx.restore();
  return canvas;
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} width
 * @param {number} height
 * @param {string} text
 * @param {() => number} random
 * @returns {TextLayout}
 */
function layoutWavyText(ctx, width, height, text, random) {
  const fontSize = WATERMARK_CONFIG.macro.fontSize;
  const diagonal = Math.hypot(width, height);
  const lineCount = 2 * Math.ceil(diagonal / fontSize);
  const waves = generateWaves(fontSize, random);
  const cycle = splitGraphemes(text);
  cycle.push(" ", "*", " ");
  const rotation = HALF_TURN_START + random() * Math.PI;
  const glyphs = [];

  ctx.font = `${fontSize}px ${WATERMARK_CONFIG.macro.fontFamily}`;

  for (let row = 0; row < lineCount; row++) {
    const baseY = row * fontSize - diagonal / 2;
    const rowOffset = WATERMARK_CONFIG.macro.rowOffsetMultiplier * fontSize;
    const horizontalOffset = (row % 2) * rowOffset;
    const startX = -diagonal / 2;
    const endX = diagonal * 1.5;
    let x = startX + horizontalOffset;
    let cycleIndex = (4 * row) % cycle.length;

    while (x < endX) {
      const char = cycle[cycleIndex];
      const advance = ctx.measureText(char).width;
      const centerX = x + advance / 2;
      const yOffset = getYAtX(centerX, startX, endX, waves);
      const angle = getAngleAtX(centerX, startX, endX, waves);
      glyphs.push({
        char,
        x: centerX,
        y: baseY + yOffset,
        angle,
      });

      x += advance;
      cycleIndex = (cycleIndex + 1) % cycle.length;
    }
  }

  return { glyphs, rotation };
}

/**
 * @param {number} fontSize
 * @param {() => number} random
 * @returns {Wave[]}
 */
function generateWaves(fontSize, random) {
  const minAmplitude =
    WATERMARK_CONFIG.macro.minAmplitudeMultiplier * fontSize;
  const maxAmplitude =
    WATERMARK_CONFIG.macro.maxAmplitudeMultiplier * fontSize;
  const waves = [];

  for (let index = 0; index < WATERMARK_CONFIG.macro.waveCount; index++) {
    const periodRange =
      WATERMARK_CONFIG.macro.maxPeriod - WATERMARK_CONFIG.macro.minPeriod;
    const period = WATERMARK_CONFIG.macro.minPeriod + random() * periodRange;
    const phase = FULL_CIRCLE * random();
    const amplitude = minAmplitude + random() * (maxAmplitude - minAmplitude);
    const weight = 0.5 + 0.5 * random();
    waves.push({ period, phase, amplitude, weight });
  }

  return waves;
}

/**
 * @param {number} x
 * @param {number} startX
 * @param {number} endX
 * @param {Wave[]} waves
 * @returns {number}
 */
function getYAtX(x, startX, endX, waves) {
  let result = 0;
  let weightedValue = 0;
  let totalWeight = 0;
  const normalizedX = (x - startX) / (endX - startX);

  for (let i = 0, total = waves.length; i < total; i++) {
    const wave = waves[i];
    const angle = FULL_CIRCLE * (normalizedX / wave.period) + wave.phase;
    weightedValue += Math.sin(angle) * wave.amplitude * wave.weight;
    totalWeight += wave.weight;
  }

  if (totalWeight !== 0) {
    result = weightedValue / totalWeight;
  }

  return result;
}

/**
 * @param {number} x
 * @param {number} startX
 * @param {number} endX
 * @param {Wave[]} waves
 * @returns {number}
 */
function getAngleAtX(x, startX, endX, waves) {
  const previousY = getYAtX(x - 1, startX, endX, waves);
  const nextY = getYAtX(x + 1, startX, endX, waves);
  return Math.atan((nextY - previousY) / 2);
}

/**
 * @param {number} width
 * @param {number} height
 * @param {string} text
 * @param {() => number} random
 * @returns {HTMLCanvasElement}
 */
function drawMicrotextLayer(width, height, text, random) {
  const canvas = createCanvas(width, height);
  const ctx = get2dContext(canvas);
  const diagonal = Math.hypot(width, height);
  const lineSpacing = WATERMARK_CONFIG.micro.lineSpacing;
  const lineCount = Math.ceil((2 * diagonal) / lineSpacing);
  const rotation =
    HALF_TURN_START + random() * Math.PI + WATERMARK_CONFIG.micro.angleOffset;
  const cycle = splitGraphemes(`${text} - SAFER COPY - `);

  ctx.save();
  ctx.font = `${WATERMARK_CONFIG.micro.fontSize}px ${WATERMARK_CONFIG.micro.fontFamily}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#000000";
  ctx.translate(width / 2, height / 2);
  ctx.rotate(rotation);

  for (let row = 0; row < lineCount; row++) {
    let x = -diagonal;
    const y = row * lineSpacing - diagonal;
    let cycleIndex = row % cycle.length;

    while (x < diagonal) {
      const char = cycle[cycleIndex];
      const advance = ctx.measureText(char).width;
      ctx.fillText(char, x + advance / 2, y);
      x += advance;
      cycleIndex = (cycleIndex + 1) % cycle.length;
    }
  }

  ctx.restore();
  return canvas;
}

/**
 * @param {HTMLCanvasElement} sourceCanvas
 * @param {() => number} random
 * @returns {HTMLCanvasElement}
 */
function distortLayer(sourceCanvas, random) {
  const width = sourceCanvas.width;
  const height = sourceCanvas.height;
  const outputCanvas = createCanvas(width, height);
  const sourceCtx = get2dContext(sourceCanvas, { willReadFrequently: true });
  const outputCtx = get2dContext(outputCanvas, { willReadFrequently: true });
  const sourceImageData = sourceCtx.getImageData(0, 0, width, height);
  const outputImageData = outputCtx.createImageData(width, height);
  const bulges = createBulges(width, height, random);
  const flowSeed = FULL_CIRCLE * random();

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const outputIndex = (y * width + x) * CHANNEL_COUNT;
      const samplePoint = getDistortedSamplePoint(
        x,
        y,
        width,
        height,
        bulges,
        flowSeed,
      );
      sampleImageData(
        sourceImageData,
        samplePoint.x,
        samplePoint.y,
        outputImageData.data,
        outputIndex,
      );
    }
  }

  outputCtx.putImageData(outputImageData, 0, 0);
  return outputCanvas;
}

/**
 * @param {number} width
 * @param {number} height
 * @param {() => number} random
 * @returns {Bulge[]}
 */
function createBulges(width, height, random) {
  const minDistance = WATERMARK_CONFIG.bulge.minDistance;
  const canvasArea = width * height;
  const distanceArea = minDistance * minDistance;
  const estimatedCount = Math.ceil(
    canvasArea / (distanceArea * WATERMARK_CONFIG.bulge.targetDensity),
  );
  const targetCount = Math.max(3, estimatedCount);
  const maxAttempts = targetCount * WATERMARK_CONFIG.bulge.attemptsPerBulge;
  const bulges = [];

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (bulges.length >= targetCount) {
      break;
    }

    const candidate = {
      x: random() * width,
      y: random() * height,
      radius:
        WATERMARK_CONFIG.bulge.minRadius +
        random() *
          (WATERMARK_CONFIG.bulge.maxRadius -
            WATERMARK_CONFIG.bulge.minRadius),
      strength:
        WATERMARK_CONFIG.bulge.minStrength +
        random() *
          (WATERMARK_CONFIG.bulge.maxStrength -
            WATERMARK_CONFIG.bulge.minStrength),
    };

    if (!isBulgeFarEnough(candidate, bulges, minDistance)) {
      continue;
    }

    bulges.push(candidate);
  }

  return bulges;
}

/**
 * @param {Bulge} candidate
 * @param {Bulge[]} bulges
 * @param {number} minDistance
 * @returns {boolean}
 */
function isBulgeFarEnough(candidate, bulges, minDistance) {
  let result = true;
  for (let i = 0, total = bulges.length; i < total; i++) {
    const bulge = bulges[i];
    const distance = Math.hypot(candidate.x - bulge.x, candidate.y - bulge.y);
    if (distance >= minDistance) {
      continue;
    }

    result = false;
    break;
  }

  return result;
}

/**
 * @param {number} x
 * @param {number} y
 * @param {number} width
 * @param {number} height
 * @param {Bulge[]} bulges
 * @param {number} flowSeed
 * @returns {{ x: number, y: number }}
 */
function getDistortedSamplePoint(x, y, width, height, bulges, flowSeed) {
  const flow = getSmoothFlow(x, y, width, height, flowSeed);
  let sampleX = x + flow.x;
  let sampleY = y + flow.y;

  for (let i = 0, total = bulges.length; i < total; i++) {
    const bulge = bulges[i];
    const deltaX = sampleX - bulge.x;
    const deltaY = sampleY - bulge.y;
    const distance = Math.hypot(deltaX, deltaY);
    const outsideBulge = distance >= bulge.radius || distance <= 0.001;
    if (outsideBulge) {
      continue;
    }

    const percent = distance / bulge.radius;
    const exponent = 1 + bulge.strength * 0.75;
    const factor = Math.pow(percent, exponent) * bulge.radius / distance;
    const mixAmount = 1 - percent;
    const scaledFactor = 1 + (factor - 1) * mixAmount;
    sampleX = bulge.x + deltaX * scaledFactor;
    sampleY = bulge.y + deltaY * scaledFactor;
  }

  return { x: sampleX, y: sampleY };
}

/**
 * @param {number} x
 * @param {number} y
 * @param {number} width
 * @param {number} height
 * @param {number} seed
 * @returns {{ x: number, y: number }}
 */
function getSmoothFlow(x, y, width, height, seed) {
  const normalizedX = x / Math.max(1, width);
  const normalizedY = y / Math.max(1, height);
  const flowX = Math.sin(
    normalizedY * FULL_CIRCLE * WATERMARK_CONFIG.flow.frequencyX + seed,
  );
  const flowY = Math.cos(
    normalizedX * FULL_CIRCLE * WATERMARK_CONFIG.flow.frequencyY - seed * 0.7,
  );
  return {
    x: flowX * WATERMARK_CONFIG.flow.amplitude,
    y: flowY * WATERMARK_CONFIG.flow.amplitude,
  };
}

/**
 * @param {ImageData} imageData
 * @param {number} x
 * @param {number} y
 * @param {Uint8ClampedArray} output
 * @param {number} outputIndex
 * @returns {void}
 */
function sampleImageData(imageData, x, y, output, outputIndex) {
  const maxX = imageData.width - 1;
  const maxY = imageData.height - 1;
  if (x < 0 || y < 0 || x > maxX || y > maxY) {
    output[outputIndex + RED_CHANNEL] = 0;
    output[outputIndex + GREEN_CHANNEL] = 0;
    output[outputIndex + BLUE_CHANNEL] = 0;
    output[outputIndex + ALPHA_CHANNEL] = 0;
    return;
  }

  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const x1 = Math.min(x0 + 1, maxX);
  const y1 = Math.min(y0 + 1, maxY);
  const tx = x - x0;
  const ty = y - y0;
  const topLeftIndex = (y0 * imageData.width + x0) * CHANNEL_COUNT;
  const topRightIndex = (y0 * imageData.width + x1) * CHANNEL_COUNT;
  const bottomLeftIndex = (y1 * imageData.width + x0) * CHANNEL_COUNT;
  const bottomRightIndex = (y1 * imageData.width + x1) * CHANNEL_COUNT;
  const data = imageData.data;

  for (let channel = 0; channel < CHANNEL_COUNT; channel++) {
    const topLeft = data[topLeftIndex + channel];
    const topRight = data[topRightIndex + channel];
    const bottomLeft = data[bottomLeftIndex + channel];
    const bottomRight = data[bottomRightIndex + channel];
    const top = topLeft + (topRight - topLeft) * tx;
    const bottom = bottomLeft + (bottomRight - bottomLeft) * tx;
    output[outputIndex + channel] = top + (bottom - top) * ty;
  }
}

/**
 * @param {HTMLCanvasElement} baseCanvas
 * @param {WatermarkLayer[]} layers
 * @returns {void}
 */
function composeAdaptiveWatermarkLayers(baseCanvas, layers) {
  const width = baseCanvas.width;
  const height = baseCanvas.height;
  const baseCtx = get2dContext(baseCanvas, { willReadFrequently: true });
  const baseImageData = baseCtx.getImageData(0, 0, width, height);
  const baseData = baseImageData.data;

  for (
    let layerIndex = 0, total = layers.length;
    layerIndex < total;
    layerIndex++
  ) {
    const layer = layers[layerIndex];
    const layerCtx = get2dContext(layer.canvas, { willReadFrequently: true });
    const layerImageData = layerCtx.getImageData(0, 0, width, height);
    composeAdaptiveLayer(baseData, layerImageData.data, layer);
  }

  baseCtx.putImageData(baseImageData, 0, 0);
}

/**
 * @param {Uint8ClampedArray} baseData
 * @param {Uint8ClampedArray} layerData
 * @param {WatermarkLayer} layer
 * @returns {void}
 */
function composeAdaptiveLayer(baseData, layerData, layer) {
  for (let i = 0, total = baseData.length; i < total; i += CHANNEL_COUNT) {
    const watermarkAlpha = layerData[i + ALPHA_CHANNEL] / MAX_CHANNEL_VALUE;
    if (watermarkAlpha <= 0) {
      continue;
    }

    const amount = watermarkAlpha * layer.alpha;
    const red = baseData[i + RED_CHANNEL];
    const green = baseData[i + GREEN_CHANNEL];
    const blue = baseData[i + BLUE_CHANNEL];
    const luminance =
      red * LUMINANCE_RED + green * LUMINANCE_GREEN + blue * LUMINANCE_BLUE;
    const target = luminance > LUMINANCE_THRESHOLD ? 0 : MAX_CHANNEL_VALUE;
    const shiftedRed = mix(red, target, amount);
    const shiftedGreen = mix(green, target, amount);
    const shiftedBlue = mix(blue, target, amount);

    baseData[i + RED_CHANNEL] = clampChannel(shiftedRed);
    baseData[i + GREEN_CHANNEL] = clampChannel(shiftedGreen);
    baseData[i + BLUE_CHANNEL] = clampChannel(shiftedBlue);
  }
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {string} watermarkText
 * @returns {void}
 */
function drawBottomWatermarkCode(canvas, watermarkText) {
  const text = normalizeWatermarkText(watermarkText);
  const ctx = get2dContext(canvas);
  const maxWidth = canvas.width - WATERMARK_CONFIG.borderSize * 2;
  const fontSize = getFittingFontSize(ctx, text, maxWidth);
  const displayText = getFittedText(ctx, text, maxWidth);
  const y =
    canvas.height - WATERMARK_CONFIG.bottomBorderSize / 2;

  ctx.save();
  ctx.font = `700 ${fontSize}px ${WATERMARK_CONFIG.bottomText.fontFamily}`;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.lineJoin = "round";
  ctx.lineWidth = WATERMARK_CONFIG.bottomText.strokeWidth;
  ctx.strokeStyle = "#ffffff";
  ctx.fillStyle = "#000000";
  ctx.strokeText(displayText, WATERMARK_CONFIG.borderSize, y);
  ctx.fillText(displayText, WATERMARK_CONFIG.borderSize, y);
  ctx.restore();
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} text
 * @param {number} maxWidth
 * @returns {number}
 */
function getFittingFontSize(ctx, text, maxWidth) {
  let result = WATERMARK_CONFIG.bottomText.minFontSize;

  for (
    let size = WATERMARK_CONFIG.bottomText.maxFontSize;
    size >= WATERMARK_CONFIG.bottomText.minFontSize;
    size--
  ) {
    ctx.font = `700 ${size}px ${WATERMARK_CONFIG.bottomText.fontFamily}`;
    const width = ctx.measureText(text).width;
    if (width > maxWidth) {
      continue;
    }

    result = size;
    break;
  }

  ctx.font = `700 ${result}px ${WATERMARK_CONFIG.bottomText.fontFamily}`;
  return result;
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} text
 * @param {number} maxWidth
 * @returns {string}
 */
function getFittedText(ctx, text, maxWidth) {
  let result = text;
  if (ctx.measureText(result).width <= maxWidth) {
    return result;
  }

  const ellipsis = "...";
  for (let length = text.length - 1; length > 0; length--) {
    const candidate = `${text.slice(0, length)}${ellipsis}`;
    if (ctx.measureText(candidate).width > maxWidth) {
      continue;
    }

    result = candidate;
    break;
  }

  return result;
}

/**
 * @param {number} width
 * @param {number} height
 * @returns {HTMLCanvasElement}
 */
function createCanvas(width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  return canvas;
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {CanvasRenderingContext2DSettings=} options
 * @returns {CanvasRenderingContext2D}
 */
function get2dContext(canvas, options) {
  const ctx = canvas.getContext("2d", options);
  if (ctx === null) {
    throw new Error("Could not create canvas context");
  }

  return ctx;
}

/**
 * @param {string} text
 * @returns {string[]}
 */
function splitGraphemes(text) {
  if (!("Segmenter" in Intl)) {
    return Array.from(text);
  }

  const segmenter = new Intl.Segmenter(undefined, {
    granularity: "grapheme",
  });
  return Array.from(segmenter.segment(text), function getSegment(item) {
    return item.segment;
  });
}

/**
 * @returns {number}
 */
function random01() {
  const values = new Uint32Array(1);
  crypto.getRandomValues(values);
  return values[0] / 0x100000000;
}

/**
 * @param {number} from
 * @param {number} to
 * @param {number} amount
 * @returns {number}
 */
function mix(from, to, amount) {
  return from * (1 - amount) + to * amount;
}

/**
 * @param {number} value
 * @returns {number}
 */
function clampChannel(value) {
  return Math.min(MAX_CHANNEL_VALUE, Math.max(0, Math.round(value)));
}
